// Owner decision O-1 (multi-entity workforce): access to "all companies,
// including ones created later" is for owners only. This panel lists everyone
// else who still has it (granted before the rule) and lets the owner convert
// them to TODAY's companies by name — nobody loses access today; a company
// created later is simply no longer automatic.
//
// Four states, kept distinct: loading, error (never shown as "nothing to do"),
// empty, data. Renders nothing at all when there is nothing to convert.
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Building2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { getAllCompaniesAccess, nameAllCompanies, type AllCompaniesPlan } from '@/services/rbacService';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; variant: 'error' | 'denied'; message: string }
  | { kind: 'data'; data: AllCompaniesPlan };

/** One-line summary of what converting does. Pure. */
export function conversionSummary(p: AllCompaniesPlan): string {
  const n = p.count;
  const what = `${n} ${n === 1 ? 'access grant covers' : 'access grants cover'}`;
  const companies = `${p.companies.length} ${p.companies.length === 1 ? 'company' : 'companies'}`;
  return `${what} every company, including ones created later. Converting names today's ${companies} instead — nobody loses access today.`;
}

interface Props {
  reloadKey?: number;
  nameOf?: (userId: string) => string;
  onChanged?: () => void;
}

export const AllCompaniesAccessPanel: React.FC<Props> = ({ reloadKey = 0, nameOf, onChanged }) => {
  const confirm = useConfirm();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'data', data: await getAllCompaniesAccess() });
    } catch (e) {
      setState({ kind: 'error', variant: errorVariant(e), message: getApiErrorMessage(e, 'Could not check all-companies access.') });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  if (state.kind === 'data' && state.data.count === 0) return null;
  if (state.kind === 'error' && state.variant === 'denied') return null; // not for this viewer

  const convert = async (p: AllCompaniesPlan) => {
    const ok = await confirm({
      title: 'Name today’s companies',
      message: `${conversionSummary(p)} Only the owner can do this.`,
      confirmLabel: 'Convert',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await nameAllCompanies();
      toast.success(`Converted — access now names ${res.companies.length} ${res.companies.length === 1 ? 'company' : 'companies'}.`);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not convert all-companies access.'), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="all-companies-access">
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-start gap-2">
          <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div>
            <h3 className="text-h3 text-foreground">Access to every company, including future ones</h3>
            <p className="text-sm text-muted-foreground">
              Only owners keep access to companies created later. Everyone else should name the companies they work with.
            </p>
          </div>
        </div>
        {state.kind === 'loading' && <DataState compact variant="loading" message="Checking all-companies access…" />}
        {state.kind === 'error' && (
          <DataState variant={state.variant} title="Not checked" message={state.message} onRetry={() => void load()} />
        )}
        {state.kind === 'data' && (
          <>
            <p className="text-sm text-foreground">{conversionSummary(state.data)}</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              {state.data.assignments.map((a) => (
                <li key={`${a.userId}:${a.roleId}`} data-testid="all-companies-row">
                  {nameOf?.(a.userId) || `Someone (${a.userId.slice(0, 8)})`} · role {a.roleKey}
                </li>
              ))}
              {state.data.grants.map((g) => (
                <li key={g.id} data-testid="all-companies-row">
                  {g.subjectKind === 'user' ? nameOf?.(g.subjectKey) || `Someone (${g.subjectKey.slice(0, 8)})` : `${g.subjectKind} ${g.subjectKey}`} · {g.permissionGroup} access
                </li>
              ))}
            </ul>
            <Button size="sm" onClick={() => void convert(state.data)} disabled={busy || state.data.companies.length === 0}>
              Name today’s companies
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
